import React, { useLayoutEffect, useRef, useState } from "react";

interface AutoScaleTextProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "className"> {
  children: React.ReactNode;
  outerClassName?: string;
  innerClassName?: string;
  transformOrigin?: string;
}

export const AutoScaleText = ({
  children,
  outerClassName = "",
  innerClassName = "",
  transformOrigin = "left",
  ...rest
}: AutoScaleTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    // Track if component is still mounted
    let isMounted = true;

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const measureAndScale = () => {
      if (!isMounted) {
        return;
      }

      const container = containerRef.current;
      const text = textRef.current;
      if (!container || !text) {
        return;
      }

      setScale(1);
      // Force a reflow to ensure the browser updates the element dimensions
      void text.offsetHeight;

      const textWidth = text.scrollWidth;
      const textHeight = text.scrollHeight;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const scaleX = containerWidth / textWidth;
      const scaleY = containerHeight / textHeight;
      const newScale = Math.min(scaleX, scaleY, 1);

      setScale(newScale);
    };

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (isMounted) {
          measureAndScale();
        }
      });
    } else {
      measureAndScale();
    }

    const resizeObserver = new ResizeObserver(() => {
      if (isMounted) {
        measureAndScale();
      }
    });

    resizeObserver.observe(containerRef.current);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      isMounted = false;
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <div
      ref={containerRef}
      className={outerClassName}
      style={{
        containerType: "inline-size", // Only constrains width
      }}
    >
      <div
        ref={textRef}
        className={innerClassName}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: transformOrigin,
          whiteSpace: "nowrap",
          display: "inline-block",
        }}
        {...rest}
      >
        {children}
      </div>
    </div>
  );
};
